import React, { useEffect, useMemo, useState } from 'react';
import {
  X, CircleNotch, ArrowCounterClockwise,
  Flag, ArrowsClockwise, Check, Warning,
} from '@phosphor-icons/react';
import axios from 'axios';

import {
  drawingClaimEdit,
  drawingClaimFlag,
  drawingClaimRerunImpact,
  drawingClaimRevert,
  drawingImpactReportResolve,
  type ClaimEditClassification,
  type ClaimEditScopeChangeDetail,
  type DrawingClaimEditResponse,
  type DrawingClaimFlagResponse,
  type DrawingClaimRevertResponse,
  type DrawingImpactReport,
} from '@/api/client';
import type { Claim } from '@/types';
import { useAppStore } from '@/store';
import { useDrawingStore } from '@/store/drawingStore';

interface Props {
  matterId: string;
}

const inputClass =
  'flex w-full rounded-md border border-zinc-300 bg-white px-3 py-2 ' +
  'text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]';

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-[#002FA7] px-4 ' +
  'text-sm font-medium text-white hover:bg-[#002277] disabled:opacity-50 transition-colors';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

const dangerBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-amber-300 ' +
  'bg-amber-50 px-3 text-sm font-medium text-amber-800 hover:bg-amber-100 ' +
  'disabled:opacity-50 transition-colors';

const CLASSIFICATIONS: { value: ClaimEditClassification; label: string }[] = [
  { value: 'typo', label: 'Typo' },
  { value: 'antecedent', label: 'Antecedent fix (a → the)' },
  { value: 'formatting', label: 'Formatting / whitespace' },
  { value: 'reword', label: 'Reword (synonym swap)' },
];

/**
 * Drawing module — In-line claim edit modal (Phase 9 slice 9E).
 *
 * Two-pane affordance for the four backend endpoints landed in
 * 9B / 9C / 9D:
 *   POST /drawing/claims/{id}/edit         — slice 9B validator-gated edit
 *   POST /drawing/claims/{id}/rerun-impact — slice 9B impact retry
 *   POST /drawing/claims/{id}/revert       — slice 9C pop-and-restore
 *   POST /drawing/impact-reports/{id}/resolve — slice 9D status flip
 *
 * Three states surface in the body:
 *   draft   — textarea + classification dropdown; submit fires
 *             /edit. 400 ``scope_change_detected`` renders the
 *             violations list inline with a "Flag for revision"
 *             button (placeholder for slice 9F's flag flow — for
 *             now it just informs the drafter what to do).
 *   review  — submit succeeded; render the impact report. Buttons:
 *             "Resolve impact", "Re-run impact" (when impact_error),
 *             "Revert this edit" (calls slice 9C).
 *   reverted — terminal state after revert; "Edit again" returns
 *              to draft.
 *
 * The modal owns local state for these flows; it does NOT lift
 * impact reports into the store because the right-panel reads them
 * via ``getDrawingImpactReports`` independently. On close the parent
 * (DrawingPage) refreshes lists so any badges update.
 */
export default function ClaimEditModal({ matterId }: Props) {
  const claimId = useDrawingStore((s) => s.claimEditModalClaimId);
  const closeModal = useDrawingStore((s) => s.closeClaimEditModal);
  const claims = useAppStore((s) => s.matterState?.claims);

  const claim = useMemo<Claim | null>(() => {
    if (!claimId || !claims) return null;
    return claims.find((c) => c.id === claimId) ?? null;
  }, [claimId, claims]);

  const [view, setView] = useState<'draft' | 'review' | 'reverted' | 'flagged'>('draft');
  const [draftText, setDraftText] = useState('');
  const [classification, setClassification] = useState<ClaimEditClassification | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<string[] | null>(null);
  const [editResult, setEditResult] = useState<DrawingClaimEditResponse | null>(null);
  const [revertResult, setRevertResult] = useState<DrawingClaimRevertResponse | null>(null);
  const [flagResult, setFlagResult] = useState<DrawingClaimFlagResponse | null>(null);
  const [latestReport, setLatestReport] = useState<DrawingImpactReport | null>(null);

  // Fresh modal-open lifecycle: reset state when the claim id
  // changes. Doesn't reset on close → null so the closing animation
  // (none today) doesn't clear the success state mid-fade.
  useEffect(() => {
    if (!claimId) return;
    setView('draft');
    setDraftText(claim?.claim_text ?? '');
    setClassification('');
    setSubmitting(false);
    setError(null);
    setViolations(null);
    setEditResult(null);
    setRevertResult(null);
    setFlagResult(null);
    setLatestReport(null);
  }, [claimId, claim?.claim_text]);

  if (!claimId) return null;

  if (!claim) {
    return (
      <ModalShell onClose={closeModal} title="Claim not found">
        <p className="text-sm text-zinc-600">
          The claim you tried to edit is no longer in the matter
          state. Close this dialog and refresh.
        </p>
      </ModalShell>
    );
  }

  const dirty = draftText.trim() !== (claim.claim_text || '').trim();
  const canSubmit = dirty && !submitting;

  async function onSubmit() {
    if (!claimId) return;
    setSubmitting(true);
    setError(null);
    setViolations(null);
    try {
      const body = {
        new_text: draftText,
        ...(classification ? { edit_classification: classification } : {}),
      };
      const result = await drawingClaimEdit(matterId, claimId, body);
      setEditResult(result);
      setLatestReport(result.impact_report);
      setView('review');
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 400) {
        const detail = e.response.data?.detail as
          | ClaimEditScopeChangeDetail
          | { error_code?: string; detail?: string }
          | undefined;
        if (detail && 'violations' in detail && Array.isArray(detail.violations)) {
          setViolations(detail.violations);
          setError(
            detail.detail ||
              'Proposed edit appears to change claim scope.',
          );
        } else if (detail?.error_code === 'no_op_edit') {
          setError('No change to submit.');
        } else if (detail?.error_code === 'invalid_classification') {
          setError(detail.detail || 'Invalid classification.');
        } else {
          setError(typeof e.response?.data === 'string'
            ? e.response.data
            : detail?.detail || 'Edit failed.');
        }
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Edit failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onRerunImpact() {
    if (!claimId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await drawingClaimRerunImpact(matterId, claimId);
      setLatestReport(result.impact_report);
      setEditResult((prev) =>
        prev ? { ...prev, impact_report: result.impact_report, impact_error: null } : prev,
      );
    } catch (e: unknown) {
      setError(humanError(e, 'Re-run impact failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onRevert() {
    if (!claimId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await drawingClaimRevert(matterId, claimId);
      setRevertResult(result);
      setView('reverted');
    } catch (e: unknown) {
      setError(humanError(e, 'Revert failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResolveImpact() {
    if (!latestReport) return;
    setSubmitting(true);
    setError(null);
    try {
      const refreshed = await drawingImpactReportResolve(
        matterId, latestReport.id,
      );
      setLatestReport(refreshed);
    } catch (e: unknown) {
      setError(humanError(e, 'Resolve failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  function onEditAgain() {
    setView('draft');
    setDraftText(revertResult?.claim?.claim_text || claim?.claim_text || '');
    setClassification('');
    setError(null);
    setViolations(null);
    setEditResult(null);
    setRevertResult(null);
    setFlagResult(null);
    setLatestReport(null);
  }

  async function onFlagForRevision() {
    if (!claimId) return;
    setSubmitting(true);
    setError(null);
    try {
      // The drafter's reason is the textarea contents — even though
      // the validator rejected those exact words as a scope change,
      // the proposed text often IS the rationale (it spells out the
      // intent). Trimming + capping mirrors the backend's
      // ClaimFlagRequest body (Optional[str], <= 2000 chars).
      const reason = draftText.trim().slice(0, 2000) || undefined;
      const result = await drawingClaimFlag(matterId, claimId, { reason });
      setFlagResult(result);
      setView('flagged');
    } catch (e: unknown) {
      setError(humanError(e, 'Flag for revision failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      onClose={closeModal}
      title={`Edit claim ${claim.claim_number}`}
      subtitle={
        view === 'flagged'
          ? 'Flagged for revision — claim unfrozen, redirect available.'
          : view === 'reverted'
            ? 'Reverted to prior text.'
            : view === 'review'
              ? 'Edit applied — review impact analysis.'
              : 'Validator-gated. Scope changes are rejected.'
      }
    >
      {view === 'draft' && (
        <DraftView
          claim={claim}
          draftText={draftText}
          onDraftTextChange={setDraftText}
          classification={classification}
          onClassificationChange={setClassification}
          submitting={submitting}
          error={error}
          violations={violations}
          canSubmit={canSubmit}
          onSubmit={onSubmit}
          onFlagForRevision={onFlagForRevision}
          onCancel={closeModal}
        />
      )}
      {view === 'review' && editResult && (
        <ReviewView
          claim={claim}
          editResult={editResult}
          latestReport={latestReport}
          submitting={submitting}
          error={error}
          onRerunImpact={onRerunImpact}
          onRevert={onRevert}
          onResolveImpact={onResolveImpact}
          onClose={closeModal}
        />
      )}
      {view === 'reverted' && revertResult && (
        <RevertedView
          revertResult={revertResult}
          submitting={submitting}
          error={error}
          onEditAgain={onEditAgain}
          onClose={closeModal}
        />
      )}
      {view === 'flagged' && flagResult && (
        <FlaggedView
          flagResult={flagResult}
          claim={claim}
          onClose={closeModal}
        />
      )}
    </ModalShell>
  );
}


// ─── Sub-views ────────────────────────────────────────────────────


function DraftView({
  claim,
  draftText, onDraftTextChange,
  classification, onClassificationChange,
  submitting, error, violations, canSubmit,
  onSubmit, onFlagForRevision, onCancel,
}: {
  claim: Claim;
  draftText: string;
  onDraftTextChange: (v: string) => void;
  classification: ClaimEditClassification | '';
  onClassificationChange: (v: ClaimEditClassification | '') => void;
  submitting: boolean;
  error: string | null;
  violations: string[] | null;
  canSubmit: boolean;
  onSubmit: () => void;
  onFlagForRevision: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4">
      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Current text
        </h3>
        <pre className="whitespace-pre-wrap rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">
          {claim.claim_text}
        </pre>
      </section>

      <section>
        <label
          className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500"
          htmlFor="claim-edit-textarea"
        >
          Proposed text
        </label>
        <textarea
          id="claim-edit-textarea"
          value={draftText}
          onChange={(e) => onDraftTextChange(e.target.value)}
          rows={6}
          className={inputClass + ' font-mono text-xs'}
          spellCheck={false}
          data-testid="claim-edit-textarea"
        />
      </section>

      <section>
        <label
          className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500"
          htmlFor="claim-edit-classification"
        >
          Classification (optional)
        </label>
        <select
          id="claim-edit-classification"
          value={classification}
          onChange={(e) =>
            onClassificationChange(e.target.value as ClaimEditClassification | '')
          }
          className={inputClass}
          data-testid="claim-edit-classification"
        >
          <option value="">— Unspecified —</option>
          {CLASSIFICATIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-zinc-500">
          Self-reported metadata only — the validator runs the same
          rules regardless.
        </p>
      </section>

      {violations && (
        <section
          className="rounded-md border border-amber-300 bg-amber-50 p-3"
          role="alert"
          data-testid="scope-change-violations"
        >
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-amber-800">
            <Warning className="h-4 w-4" />
            Scope change detected
          </div>
          <p className="mb-2 text-xs text-amber-800">
            {error || 'The edit changes claim scope and was rejected.'}
          </p>
          <ul className="list-disc pl-5 text-xs text-amber-900">
            {violations.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-amber-700">
              For real scope changes, use <strong>Flag for revision</strong>{' '}
              to push the claim back to Stage 3.
            </p>
            <button
              type="button"
              className={dangerBtn}
              onClick={onFlagForRevision}
              disabled={submitting}
              data-testid="claim-edit-flag"
            >
              {submitting
                ? <CircleNotch className="h-4 w-4 animate-spin" />
                : <Flag className="h-4 w-4" />}
              Flag for revision
            </button>
          </div>
        </section>
      )}

      {error && !violations && (
        <p
          className="text-xs text-red-700"
          role="alert"
          data-testid="claim-edit-error"
        >
          {error}
        </p>
      )}

      <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3">
        <button
          type="button"
          className={outlineBtn}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className={primaryBtn}
          onClick={onSubmit}
          disabled={!canSubmit}
          data-testid="claim-edit-submit"
        >
          {submitting && <CircleNotch className="h-4 w-4 animate-spin" />}
          Submit edit
        </button>
      </footer>
    </div>
  );
}


function ReviewView({
  claim, editResult, latestReport, submitting, error,
  onRerunImpact, onRevert, onResolveImpact, onClose,
}: {
  claim: Claim;
  editResult: DrawingClaimEditResponse;
  latestReport: DrawingImpactReport | null;
  submitting: boolean;
  error: string | null;
  onRerunImpact: () => void;
  onRevert: () => void;
  onResolveImpact: () => void;
  onClose: () => void;
}) {
  const newText = editResult.claim?.claim_text || '';
  const impactError = editResult.impact_error;
  const isResolved = latestReport?.status === 'resolved';

  return (
    <div className="grid grid-cols-1 gap-4">
      <section
        className="rounded-md border border-emerald-300 bg-emerald-50 p-3"
        role="status"
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
          <Check className="h-4 w-4" />
          Edit applied to claim {claim.claim_number}
        </div>
        <p className="mt-1 text-xs text-emerald-800">
          Audit log id <code className="text-[11px]">{editResult.edit_log_id.slice(0, 8)}</code>.
        </p>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          New claim text
        </h3>
        <pre className="whitespace-pre-wrap rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">
          {newText}
        </pre>
      </section>

      {impactError && (
        <section
          className="rounded-md border border-amber-300 bg-amber-50 p-3"
          role="alert"
          data-testid="impact-error"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
            <Warning className="h-4 w-4" />
            Impact analysis failed
          </div>
          <p className="mt-1 text-xs text-amber-800">{impactError.detail}</p>
          <p className="mt-2 text-[11px] text-amber-700">
            The edit itself persisted. Click "Re-run impact" to retry.
          </p>
        </section>
      )}

      {latestReport && (
        <ImpactReportCard
          report={latestReport}
          onResolve={onResolveImpact}
          submitting={submitting}
          isResolved={isResolved}
        />
      )}

      {error && (
        <p
          className="text-xs text-red-700"
          role="alert"
          data-testid="review-error"
        >
          {error}
        </p>
      )}

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-3">
        <button
          type="button"
          className={dangerBtn}
          onClick={onRevert}
          disabled={submitting}
          data-testid="claim-edit-revert"
        >
          {submitting
            ? <CircleNotch className="h-4 w-4 animate-spin" />
            : <ArrowCounterClockwise className="h-4 w-4" />}
          Revert this edit
        </button>
        {(impactError || (latestReport && !isResolved)) && (
          <button
            type="button"
            className={outlineBtn}
            onClick={onRerunImpact}
            disabled={submitting}
            data-testid="claim-edit-rerun"
          >
            <ArrowsClockwise className="h-4 w-4" />
            Re-run impact
          </button>
        )}
        <button
          type="button"
          className={primaryBtn}
          onClick={onClose}
          disabled={submitting}
        >
          Done
        </button>
      </footer>
    </div>
  );
}


function RevertedView({
  revertResult, submitting, error, onEditAgain, onClose,
}: {
  revertResult: DrawingClaimRevertResponse;
  submitting: boolean;
  error: string | null;
  onEditAgain: () => void;
  onClose: () => void;
}) {
  const restoredText = (revertResult.claim?.claim_text || '') as string;
  return (
    <div className="grid grid-cols-1 gap-4">
      <section
        className="rounded-md border border-zinc-300 bg-zinc-50 p-3"
        role="status"
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-zinc-700">
          <ArrowCounterClockwise className="h-4 w-4" />
          Reverted
        </div>
        <p className="mt-1 text-xs text-zinc-600">
          Audit log id <code className="text-[11px]">{revertResult.edit_log_id.slice(0, 8)}</code>
          {revertResult.reverted_log_id && (
            <> — undoes <code className="text-[11px]">{revertResult.reverted_log_id.slice(0, 8)}</code>.</>
          )}
        </p>
      </section>
      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Restored text
        </h3>
        <pre className="whitespace-pre-wrap rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">
          {restoredText}
        </pre>
      </section>
      {error && (
        <p className="text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
      <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3">
        <button
          type="button"
          className={outlineBtn}
          onClick={onEditAgain}
          disabled={submitting}
        >
          Edit again
        </button>
        <button
          type="button"
          className={primaryBtn}
          onClick={onClose}
          disabled={submitting}
        >
          Close
        </button>
      </footer>
    </div>
  );
}


// ─── Impact-report card (shared review panel block) ───────────────


function ImpactReportCard({
  report, onResolve, submitting, isResolved,
}: {
  report: DrawingImpactReport;
  onResolve: () => void;
  submitting: boolean;
  isResolved: boolean;
}) {
  const r = report.report || {};
  return (
    <section
      className="rounded-md border border-zinc-200 bg-white p-3"
      data-testid="impact-report-card"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Impact analysis
          <span
            className={
              'ml-2 inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase ' +
              (isResolved
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-amber-100 text-amber-800')
            }
            data-testid="impact-report-status"
          >
            {report.status}
          </span>
        </h3>
        {!isResolved && (
          <button
            type="button"
            className={outlineBtn}
            onClick={onResolve}
            disabled={submitting}
            data-testid="claim-edit-resolve"
          >
            <Check className="h-4 w-4" />
            Resolve
          </button>
        )}
      </div>
      <ul className="space-y-1 text-xs text-zinc-700">
        {r.diff_classification && (
          <li>
            <span className="font-semibold">Diff:</span>{' '}
            {String(r.diff_classification)}
          </li>
        )}
        {typeof r.qc_must_rerun === 'boolean' && (
          <li>
            <span className="font-semibold">QC re-run required:</span>{' '}
            {r.qc_must_rerun ? 'yes' : 'no'}
            {r.qc_rerun_rationale && (
              <span className="ml-1 text-zinc-500">— {String(r.qc_rerun_rationale)}</span>
            )}
          </li>
        )}
        {r.revert_risk && (
          <li>
            <span className="font-semibold">Revert risk:</span>{' '}
            {String(r.revert_risk)}
            {r.revert_risk_rationale && (
              <span className="ml-1 text-zinc-500">— {String(r.revert_risk_rationale)}</span>
            )}
          </li>
        )}
        {Array.isArray(r.affected_figures) && r.affected_figures.length > 0 && (
          <li>
            <span className="font-semibold">Affected figures:</span>{' '}
            {r.affected_figures.length}
          </li>
        )}
        {Array.isArray(r.stale_numerals) && r.stale_numerals.length > 0 && (
          <li>
            <span className="font-semibold">Stale numerals:</span>{' '}
            {r.stale_numerals.length}
          </li>
        )}
        {Array.isArray(r.affected_dependent_claims) &&
          r.affected_dependent_claims.length > 0 && (
            <li>
              <span className="font-semibold">Affected dependents:</span>{' '}
              {r.affected_dependent_claims.length}
            </li>
          )}
        {r.claim_edit_log_entry && (
          <li className="italic text-zinc-500">"{String(r.claim_edit_log_entry)}"</li>
        )}
      </ul>
      {isResolved && r._resolution && (
        <p className="mt-2 text-[11px] text-emerald-700">
          Resolved by {r._resolution.resolved_by_email} at {r._resolution.resolved_at}
          {r._resolution.resolution_note && (
            <> — {r._resolution.resolution_note}</>
          )}
        </p>
      )}
    </section>
  );
}


function FlaggedView({
  flagResult, claim, onClose,
}: {
  flagResult: DrawingClaimFlagResponse;
  claim: Claim;
  onClose: () => void;
}) {
  const resolvedCount = flagResult.auto_resolved_report_ids.length;
  return (
    <div className="grid grid-cols-1 gap-4">
      <section
        className="rounded-md border border-orange-300 bg-orange-50 p-3"
        role="status"
        data-testid="claim-flagged-banner"
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-orange-800">
          <Flag className="h-4 w-4" />
          Claim {claim.claim_number} flagged for revision
        </div>
        <p className="mt-1 text-xs text-orange-800">
          {flagResult.was_frozen
            ? 'Claim has been unfrozen — drafter may now edit it in Stage 3.'
            : 'Claim was already unfrozen; flag recorded for audit.'}
        </p>
        {resolvedCount > 0 && (
          <p className="mt-1 text-[11px] text-orange-700">
            Auto-closed {resolvedCount} open impact report
            {resolvedCount === 1 ? '' : 's'} for this claim.
          </p>
        )}
      </section>
      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Audit
        </h3>
        <p className="text-xs text-zinc-600">
          Edit-log id <code className="text-[11px]">
            {flagResult.edit_log_id.slice(0, 8)}
          </code> — flag id <code className="text-[11px]">
            {flagResult.flag_id.slice(0, 8)}
          </code>.
        </p>
      </section>
      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-3">
        <a
          href={flagResult.redirect_url}
          className={primaryBtn}
          data-testid="claim-flagged-redirect"
        >
          Open in Stage 3
        </a>
        <button
          type="button"
          className={outlineBtn}
          onClick={onClose}
        >
          Close
        </button>
      </footer>
    </div>
  );
}


// ─── Modal shell ──────────────────────────────────────────────────


function ModalShell({
  title, subtitle, onClose, children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="claim-edit-modal"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-zinc-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            className="rounded p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-auto px-4 py-3">{children}</div>
      </div>
    </div>
  );
}


// ─── Helpers ──────────────────────────────────────────────────────


function humanError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'detail' in detail) {
      return String((detail as { detail: unknown }).detail);
    }
    return e.message || fallback;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

